import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, BedDouble, Bath, Wifi, Flame, Tv, UtensilsCrossed, Car, Shield, ChevronLeft, ChevronRight, ArrowRight, X } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { useBusinessInfo } from '../context/BusinessContext';
import { getImageUrl } from '../utils/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const amenityIcons = {
  'Cocina completa': UtensilsCrossed,
  'WiFi': Wifi,
  'TV': Tv,
  'Chimenea': Flame,
  'Firepit': Flame,
  'Estacionamiento': Car,
  'Seguridad 24/7': Shield
};

// Cabins List Page
export const CabinsList = () => {
  const [cabins, setCabins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchCabins = async () => {
      try {
        const response = await axios.get(`${API}/cabins`);
        setCabins(response.data);
      } catch (error) {
        console.error('Error fetching cabins:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCabins();
  }, []);

  const filteredCabins = filter === 'all' 
    ? cabins 
    : cabins.filter(c => c.model_type === filter);

  return (
    <div data-testid="cabins-list-page" className="min-h-screen bg-[#F5F2EB] pt-24">
      {/* Header */}
      <section className="py-16 bg-white">
        <div className="container-ribba">
          <div className="text-center">
            <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
              Cabañas RIBBA
            </span>
            <h1 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] font-bold mt-3 mb-4">
              {t('cabins.subtitle')}
            </h1>
            <p className="text-[#5A5A5A] max-w-2xl mx-auto mb-8">
              {t('cabins.pageDescription')}
            </p>

            {/* Filter */}
            <div className="flex justify-center gap-4">
              {[
                { value: 'all', label: t('cabins.filterAll') },
                { value: 'grande', label: t('cabins.filterLarge') },
                { value: 'mediana', label: t('cabins.filterMedium') }
              ].map((option) => (
                <button
                  key={option.value}
                  data-testid={`filter-${option.value}`}
                  onClick={() => setFilter(option.value)}
                  className={`px-6 py-2 rounded-sm text-sm font-medium transition-all ${
                    filter === option.value
                      ? 'bg-[#2F3E30] text-white'
                      : 'bg-[#F5F2EB] text-[#5A5A5A] hover:bg-[#E5E0D6]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cabins Grid */}
      <section className="py-16">
        <div className="container-ribba">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredCabins.map((cabin) => (
                <CabinCard key={cabin.id} cabin={cabin} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// Cabin Card Component
const CabinCard = ({ cabin }) => {
  const { t, language } = useLanguage();
  
  return (
    <div
      data-testid={`cabin-card-${cabin.id}`}
      className="bg-white rounded-sm overflow-hidden card-shadow cabin-card"
    >
      <div className="img-zoom aspect-cabin relative">
        <img
          src={getImageUrl(cabin.images[0])}
          alt={cabin.name}
          className="w-full h-full object-cover"
        />
        <span className="absolute top-4 left-4 badge-gold">
          {cabin.model_type === 'grande' ? t('cabins.large') : t('cabins.medium')}
        </span>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading text-2xl text-[#1A1A1A] font-bold">
            {language === 'es' 
              ? cabin.name 
              : (cabin.model_type === 'grande' ? `Large Cabin ${cabin.name.slice(-1)}` : `Medium Cabin ${cabin.name.slice(-1)}`)}
          </h3>
          <div className="text-right">
            <span className="price-tag">${cabin.price_per_night.toLocaleString()}</span>
            <span className="block text-[#5A5A5A] text-xs">{t('common.mxn')} / {language === 'es' ? 'noche' : 'night'}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-4 text-[#5A5A5A] text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#2F3E30]" />
            <span>{cabin.capacity} {t('cabins.guests')}</span>
          </div>
          <div className="flex items-center gap-2">
            <BedDouble className="w-4 h-4 text-[#2F3E30]" />
            <span>{cabin.bedrooms} {language === 'es' ? 'hab' : 'bed'}, {cabin.beds} {t('cabins.beds')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Bath className="w-4 h-4 text-[#2F3E30]" />
            <span>{cabin.bathrooms} {cabin.bathrooms > 1 ? t('cabins.bathrooms') : t('cabins.bathroom')}</span>
          </div>
        </div>

        <p className="text-[#5A5A5A] text-sm mb-4 line-clamp-2">
          {language === 'es' ? cabin.description : t('cabins.cabinDescription')}
        </p>

        <div className="flex gap-3">
          <Link
            to={`/cabanas/${cabin.id}`}
            data-testid={`ver-detalles-${cabin.id}`}
            className="flex-1 btn-secondary text-center text-sm py-3"
          >
            {t('cabins.viewDetails')}
          </Link>
          <Link
            to={`/reservar?cabin=${cabin.id}`}
            data-testid={`reservar-${cabin.id}`}
            className="flex-1 btn-primary text-center text-sm py-3"
          >
            {t('cabins.book')}
          </Link>
        </div>
      </div>
    </div>
  );
};

// Cabin Detail Page
export const CabinDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cabin, setCabin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { t, language } = useLanguage();
  const { getWhatsAppLink } = useBusinessInfo();

  useEffect(() => {
    const fetchCabin = async () => {
      try {
        const response = await axios.get(`${API}/cabins/${id}`);
        setCabin(response.data);
      } catch (error) {
        console.error('Error fetching cabin:', error);
        navigate('/cabanas');
      } finally {
        setLoading(false);
      }
    };
    fetchCabin();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F2EB]">
        <div className="spinner" />
      </div>
    );
  }

  if (!cabin) return null;

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % cabin.images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + cabin.images.length) % cabin.images.length);
  };

  const cabinDisplayName = language === 'es' 
    ? cabin.name 
    : (cabin.model_type === 'grande' ? `Large Cabin ${cabin.name.slice(-1)}` : `Medium Cabin ${cabin.name.slice(-1)}`);

  return (
    <div data-testid="cabin-detail-page" className="min-h-screen bg-[#F5F2EB] pt-24">
      <div className="container-ribba py-8">
        {/* Back button */}
        <Link
          to="/cabanas"
          className="inline-flex items-center gap-2 text-[#5A5A5A] hover:text-[#2F3E30] mb-8 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>{t('cabins.backToCabins')}</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div 
              className="relative aspect-[4/3] rounded-sm overflow-hidden cursor-pointer"
              onClick={() => setLightboxOpen(true)}
            >
              <img
                src={getImageUrl(cabin.images[currentImage])}
                alt={`${cabinDisplayName} - ${currentImage + 1}`}
                className="w-full h-full object-cover"
              />
              {cabin.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-[#2F3E30]" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-[#2F3E30]" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {cabin.images.length > 1 && (
              <div className="flex gap-3">
                {cabin.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImage(idx)}
                    className={`w-20 h-20 rounded-sm overflow-hidden border-2 transition-all ${
                      idx === currentImage ? 'border-[#2F3E30]' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <span className="badge-gold mb-4 inline-block">
              {cabin.model_type === 'grande' ? t('cabins.largeCabin') : t('cabins.mediumCabin')}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] font-bold mb-4">
              {cabinDisplayName}
            </h1>
            
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-4xl font-heading font-bold text-[#2F3E30]">
                ${cabin.price_per_night.toLocaleString()}
              </span>
              <span className="text-[#5A5A5A]">{t('common.mxn')} / {language === 'es' ? 'noche' : 'night'}</span>
            </div>

            <p className="text-[#5A5A5A] leading-relaxed mb-8">
              {language === 'es' ? cabin.description : t('cabins.cabinDescription')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white p-4 rounded-sm text-center card-shadow">
                <Users className="w-6 h-6 text-[#2F3E30] mx-auto mb-2" />
                <span className="block text-2xl font-heading font-bold text-[#1A1A1A]">{cabin.capacity}</span>
                <span className="text-[#5A5A5A] text-sm">{t('cabins.guests')}</span>
              </div>
              <div className="bg-white p-4 rounded-sm text-center card-shadow">
                <BedDouble className="w-6 h-6 text-[#2F3E30] mx-auto mb-2" />
                <span className="block text-2xl font-heading font-bold text-[#1A1A1A]">{cabin.bedrooms}</span>
                <span className="text-[#5A5A5A] text-sm">{t('cabins.bedrooms')}</span>
              </div>
              <div className="bg-white p-4 rounded-sm text-center card-shadow">
                <BedDouble className="w-6 h-6 text-[#2F3E30] mx-auto mb-2" />
                <span className="block text-2xl font-heading font-bold text-[#1A1A1A]">{cabin.beds}</span>
                <span className="text-[#5A5A5A] text-sm">{t('cabins.beds')}</span>
              </div>
              <div className="bg-white p-4 rounded-sm text-center card-shadow">
                <Bath className="w-6 h-6 text-[#2F3E30] mx-auto mb-2" />
                <span className="block text-2xl font-heading font-bold text-[#1A1A1A]">{cabin.bathrooms}</span>
                <span className="text-[#5A5A5A] text-sm">{t('cabins.bathrooms')}</span>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-8">
              <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-4">
                {t('cabins.amenities')}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {cabin.amenities.map((amenity, idx) => {
                  const Icon = amenityIcons[amenity] || Wifi;
                  return (
                    <div key={idx} className="flex items-center gap-3 text-[#5A5A5A]">
                      <Icon className="w-5 h-5 text-[#2F3E30]" />
                      <span>{amenity}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={`/reservar?cabin=${cabin.id}`}
                data-testid="reservar-cabin-btn"
                className="flex-1 btn-primary text-center flex items-center justify-center gap-2"
              >
                {t('cabins.bookThisCabin')}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={getWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn-secondary text-center"
              >
                {t('cabins.checkAvailability')}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl p-0 bg-black/95 border-none">
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="relative">
            <img
              src={getImageUrl(cabin.images[currentImage])}
              alt={cabinDisplayName}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            {cabin.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CabinsList;
